import { useMemo, useCallback } from 'react';
import dayjs from 'dayjs';
import isBetweenPlugin from 'dayjs/plugin/isBetween';
import updateLocale from 'dayjs/plugin/updateLocale';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import { TextField } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import CustomPickersDay from '../../styles/CustomPickersDay';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import palette from '../../styles/palette';
import Box from '@mui/material/Box';
import KeyDates from './KeyDates';
import useMediaQuery from '@mui/material/useMediaQuery';

dayjs.extend(isBetweenPlugin);
dayjs.extend(updateLocale);
dayjs.updateLocale('en', {
  weekStart: 1, // set the start of a week to Monday instead of Sunday。
});

const StaticWeekPicker = ({ selectedDates, availableDates, onChange }) => {
  const collisionPoint = useMediaQuery('(max-width:667px)');
  const smValue = collisionPoint ? 12 : 6;

  const parsedAvailableDates = useMemo(
    () =>
      availableDates?.map((dates) => ({
        original: dates,
        parsed: {
          start: dayjs(dates.date_start),
          end: dayjs(dates.date_end),
          closeDate: dayjs(dates.date_close),
          dueDate: dayjs(dates.date_materials),
        },
      })),
    [availableDates],
  );

  const parsedSelectedDates = useMemo(
    () => ({
      start: dayjs(selectedDates?.date_start),
      end: dayjs(selectedDates?.date_end),
      closeDate: dayjs(selectedDates?.date_close), // Last day to purchase
      dueDate: dayjs(selectedDates?.date_materials), // Final ad due date
    }),
    [selectedDates],
  );

  const styles = {
    textField: {
      width: '100%',
      marginTop: '2vh',
    },
    datePickerCard: {
      height: '38vh',
      backgroundColor: palette.gt_teal_1,
    },
    datePickerGrid: {
      backgroundColor: palette.gt_teal_1,
    },
    datePickerBox: {
      border: 1,
      borderColor: palette.gt_teal_6,
      width: '100%',
      minHeight: '30vh',
      backgroundColor: palette.white,
    },
    datePicker: {
      objectFit: 'contain',
    },
    dateTilePadding: {
      padding: '20px',
      py: '10px',
    },
  };

  const renderWeekPickerDay = useCallback(
    (date, _, pickersDayProps) => {
      if (!parsedSelectedDates) {
        return <PickersDay {...pickersDayProps} />;
      }

      const datesInterval = parsedAvailableDates?.find((dates) =>
        date.isBetween(dates.parsed.start, dates.parsed.end, null, '[]'),
      );

      const dayIsBetween = date.isBetween(
        parsedSelectedDates?.start,
        parsedSelectedDates?.end,
        null,
        '[]',
      );
      let isFirstDay = date.isSame(parsedSelectedDates?.start, 'day');

      // comparing if the start date is same as end date then we won't highlight the date
      const date1 = dayjs(parsedSelectedDates?.start);
      const date2 = dayjs(parsedSelectedDates?.end);

      date1.isSame(date2, 'day') && (isFirstDay = false);
      const id = dayIsBetween ? 'selected-date-area' : undefined;
      return (
        <CustomPickersDay
          {...pickersDayProps}
          sx={styles.dateTilePadding}
          disableMargin
          today={false}
          selected={false}
          disabled={!datesInterval}
          dayIsBetween={dayIsBetween}
          isFirstDay={isFirstDay}
          data-testid={id}
        />
      );
    },
    [parsedAvailableDates, parsedSelectedDates],
  );

  const onDateSelected = useCallback(
    (date) => {
      if (
        date.isBetween(
          parsedSelectedDates.start,
          parsedSelectedDates.end,
          null,
          '[]',
        )
      ) {
        return;
      }
      const newSelection = parsedAvailableDates?.find((dates) =>
        date.isBetween(dates.parsed.start, dates.parsed.end, null, '[]'),
      );
      if (newSelection) {
        onChange(newSelection.original);
      }
    },
    [parsedAvailableDates, parsedSelectedDates, onChange],
  );

  return (
    <Card elevation={0}>
      <Grid container direction="row">
        <Grid item xs={12} sm={smValue} sx={styles.datePickerGrid}>
          <Box sx={styles.datePickerBox}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <StaticDatePicker
                displayStaticWrapperAs="desktop"
                label="Week picker"
                orientation="landscape"
                showDaysOutsideCurrentMonth={true}
                value={parsedSelectedDates.start}
                onChange={onDateSelected}
                renderDay={renderWeekPickerDay}
                renderInput={(params) => <TextField {...params} />}
                fullWidth
                sx={styles.datePicker}
              />
            </LocalizationProvider>
          </Box>
        </Grid>
        <KeyDates selectedDates={selectedDates ? parsedSelectedDates : null} />
      </Grid>
    </Card>
  );
};

export default StaticWeekPicker;
